/**
 * 이상한 방 - 탭 좌표 → 게임 대상 판정 (아이소메트릭)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 6.1절(두 종류의 히트) · 6.2절(빈 자리 판정은 관대하게) ·
 *            3.2절(입력 UX - 표면 스냅) · 원칙 2(1절)
 *
 * ## 이 파일이 답하는 질문
 *
 * "화면의 이 점을 눌렀다. 무엇을 겨눈 것인가?"
 *
 * 탄약도 정답 여부도 점수도 여기 없다. 11.3절이 그것들을 서버의 몫으로 못박았으므로
 * 클라이언트에 남는 것은 기하 문제 하나뿐이다. 이 경계를 지켜야 2주차에 서버를 붙일 때 이 파일이 그대로 남는다.
 *
 * ## 손가락은 굵다 - 그런데 관대함을 아무 데나 뿌리면 오히려 탄환을 잃는다
 *
 * 폰에서 1px을 맞히라고 요구할 수는 없다. 그래서 레이캐스트가 빗나가면 탭 지점 주변 8방향을 재시도한다.
 * 반경은 **줌에 반비례**한다 - 줌인 상태에서는 소품이 화면 가득이라 관대함이 옆 물건을 가로채기 때문이다.
 *
 * 다만 관대함의 방향이 중요하다. 6.1절의 판정 경로는 둘이고 **서로 경쟁한다**:
 *
 *  - 오브젝트 히트 - 바뀐 소품을 직접 누름
 *  - 빈 셀 히트   - 제거된 소품이 있던 **빈 자리**를 누름
 *
 * 여기서 "가까운 오브젝트를 무조건 집어 준다"고 만들면, 빈 자리를 정확히 누른 술래의 탭이
 * 옆 의자로 끌려가 **정답이 오답이 된다.** 6.2절이 경고한 억울한 실패가 관대함 때문에 발생하는 셈이다.
 *
 * 그래서 우선순위를 이렇게 고정한다:
 *
 *  1. 중심이 히트 프록시 → 제거 히트 (프록시는 항상 정답 자리다. 관대함이 순이익인 유일한 지점)
 *  2. 중심이 오브젝트    → 오브젝트 히트 (확신을 가진 탭이다. 주변을 볼 이유가 없다)
 *  3. 주변 8방향의 프록시 → 제거 히트
 *  4. 주변 8방향의 오브젝트 → 오브젝트 히트 (중심이 바닥·벽이었을 때만 도는 구제 경로)
 *  5. 그 외 → 빈 셀 히트 (중심이 맞힌 표면의 셀)
 *
 * 4번이 "빈 자리를 눌렀는데 옆 물건으로 끌려가는" 위험을 안는 것처럼 보이지만 그렇지 않다.
 * 제거된 자리에는 **항상 히트 프록시가 있고**(11.2절: 원본 상태는 어차피 클라이언트에 있다 →
 * 제거 목록을 로컬에서 계산할 수 있다) 프록시는 3번에서 4번보다 먼저 잡힌다.
 *
 * ## 배치 모드에는 관대함을 주지 않는다
 *
 * 판정은 관대해야 하지만 배치는 정확해야 한다. "여기 놓으려고 눌렀는데 옆 칸에 놓임"은
 * 구제가 아니라 사고이고, 꾸미기 페이즈가 1분이라 되돌릴 시간도 없다.
 * 그래서 `mode: 'place'`는 중심 한 발만 쏜다.
 *
 * ## 바꾸기(사람 체인저)의 물건 고르기도 배치 쪽이다
 *
 * 같은 논리가 바꾸기 페이즈의 **물건 선택**에도 적용된다. 수색의 탭은 빗나가면 탄환을 잃지만 바꾸기의 탭은 빗나가면
 * 되돌리고 다시 누를 시간(60초에 12개)을 잃는다 - 그런데 관대함은 그 반대를 만든다. 전체 조망에서 바닥의 작은 소품을
 * 누르면 중심은 바닥에 떨어지고, 8방향 구제 경로가 투영상 겹쳐 보이는 **벽 물건(거울·TV)** 을 대신 집어 준다
 * (링 후보 중 카메라에 가까운 것이 이기고, 벽 물건은 머리 높이라 늘 가깝다). 사람 체인저 플레이테스트(로드맵 7절 M4 기록)가
 * 손 속도보다 이 오선택이 개수를 깎을 것이라고 적었다. 그래서 바꾸기 중 선택 픽은 `radiusPx: EXACT_TAP_RADIUS_PX`로
 * 링을 끈다 - 누른 것만 잡히고, 빗나간 탭은 "그 물건 말고"(선택 해제)로 떨어진다. 새 판정 경로가 아니라 기존 반경 옵션이다.
 * 수색의 관대함은 한 글자도 바뀌지 않는다.
 *
 * ## 탭 배치는 표면 스냅 전용이다
 *
 * 3.2절의 자유 높이(수정키)는 폰에 수정키가 없으므로 이 경로에 없다.
 * 데스크톱의 자유 높이 배치는 `picker.ts`가 계속 담당한다. 여기서 흉내 내면 규칙이 두 벌이 된다.
 *
 * ## 상태 변경 규약
 *
 * `ctx.raycaster`를 제자리 변경한다(렌더·입력 레이어 한정 예외). 게임 상태는 읽기만 한다.
 */

import * as THREE from 'three'

import { rotatedFootprint, worldToCell } from '../core/grid'
import type { Catalog } from '../core/room'
import type {
  CatalogEntry,
  CatalogId,
  Cell3,
  InstanceId,
  ObjectInstance,
  RoomBounds,
  RoomState,
} from '../core/types'
import { readHitProxyInfo } from '../render/hitProxy'
import { isHitCollider } from '../render/meshFactory'
import { getInstanceIdFromObject3D } from '../render/objectView'

/* --- 관대한 히트 반경 ------------------------------------------------------------ */

/**
 * 줌아웃(전체 조망) 상태의 재시도 반경(CSS px).
 *
 * 이 상태에서는 20m짜리 아파트가 폰 화면에 통째로 들어가므로 셀 하나(0.25m)가 몇 px에 불과하다.
 * 손가락 접촉면(8~10mm ≈ 30~40px)보다 작은 대상을 겨누는 셈이라 관대함 없이는 아무것도 못 맞힌다.
 */
const TAP_RADIUS_AT_MIN_ZOOM_PX = 22

/**
 * 줌인(최대 확대) 상태의 재시도 반경(CSS px).
 *
 * 여기서는 소품 하나가 화면 수백 px을 차지한다. 반경이 크면 **정확히 겨눈 탭이 옆 물건으로 끌려가고**,
 * 그 오발이 곧 탄환 손실이다. 0으로 두지 않는 이유는 손가락을 뗄 때의 미세 이동을 흡수하기 위해서다.
 */
const TAP_RADIUS_AT_MAX_ZOOM_PX = 6

/**
 * 관대함 없는 픽의 반경(CSS px). 바꾸기 중 물건 고르기가 쓴다(머리말 "바꾸기의 물건 고르기도 배치 쪽이다").
 * 0이면 링을 돌지 않는다 - 중심과 같은 점을 여덟 번 더 쏘는 것은 답을 바꾸지 못하고 광선 비용만 여덟 배다.
 */
export const EXACT_TAP_RADIUS_PX = 0

/**
 * 주변 재시도 방향 8개(단위 벡터).
 * 대각선을 √½로 줄여 실제 원 위에 놓는다 - 정사각형으로 두면 대각선만 1.41배 관대해져서
 * "오른쪽 아래로 빗나갔을 때만 잘 잡히는" 비대칭이 생긴다.
 */
const RING_DIRECTIONS: readonly (readonly [number, number])[] = [
  [0, -1],
  [Math.SQRT1_2, -Math.SQRT1_2],
  [1, 0],
  [Math.SQRT1_2, Math.SQRT1_2],
  [0, 1],
  [-Math.SQRT1_2, Math.SQRT1_2],
  [-1, 0],
  [-Math.SQRT1_2, -Math.SQRT1_2],
]

/* --- 좌표 변환 ----------------------------------------------------------------- */

/** NDC(정규화 장치 좌표)는 [-1, 1] 구간이다. 픽셀 비율 → NDC 변환 계수. */
const NDC_RANGE_SCALE = 2

/** 바닥 평면의 높이(미터). 그리드 y=0 셀의 아랫면이다. */
const FLOOR_PLANE_Y = 0

/** 위쪽 축. y-up 규약은 `core/types.ts`의 `Cell3` 주석에서 온다. */
const UP_AXIS = Object.freeze({ x: 0, y: 1, z: 0 })

/**
 * 표면 히트에서 셀을 구할 때 광선 반대 방향으로 물러나는 거리(미터).
 *
 * 교차점은 면 **위**에 정확히 놓이는데 `worldToCell`은 내림이라 경계값이 면 뒤쪽 셀로 떨어질 수 있다.
 * 바닥을 눌렀는데 셀 y가 -1이 되는 식이다. 셀 크기(0.25m)에 비해 무시할 만큼만 물러나 그 경계를 끊는다.
 */
const SURFACE_BACKOFF_METERS = 0.001

/* --- 공개 타입 ----------------------------------------------------------------- */

/**
 * 이 모듈이 실제로 쓰는 것만 추린 렌더 컨텍스트.
 * `render/scene.ts`의 `SceneCtx`가 그대로 대입된다(구조적 부분집합).
 *
 * 굳이 좁히는 이유는 둘이다. 테스트가 WebGL 렌더러 없이 진짜 객체를 만들 수 있고,
 * 카메라 종류를 가리지 않으므로 `isoCamera`가 따로 들고 있는 카메라를 쓰고 싶을 때
 * `pickAt({ ...ctx, camera: iso.camera }, ...)`처럼 캐스팅 없이 갈아 끼울 수 있다.
 */
export type PickerCtx = Readonly<{
  scene: THREE.Object3D
  camera: THREE.Camera
  renderer: Readonly<{ domElement: Readonly<{ getBoundingClientRect: () => ViewportRect }> }>
  raycaster: THREE.Raycaster
}>

/** 캔버스의 화면상 위치·크기. `DOMRect`가 그대로 대입되는 최소 부분집합이다. */
export type ViewportRect = Readonly<{
  left: number
  top: number
  width: number
  height: number
}>

/** 월드 좌표 한 점. 결과가 불변이어야 하므로 `Vector3`를 그대로 내보내지 않는다. */
export type WorldPoint = Readonly<{ x: number; y: number; z: number }>

/**
 * 탭의 해석 모드.
 *  - `search` - 수색 페이즈. 6.1절의 두 히트 경로로 해석한다.
 *  - `place`  - 꾸미기(에디터). 같은 탭이 배치 지점 지정으로 동작한다.
 */
export type PickMode = 'search' | 'place'

export type PickOptions = Readonly<{
  /** 생략하면 `'search'`. */
  mode?: PickMode
  /** 정규화 줌(0 = 전체 조망, 1 = 최대 확대). 관대한 반경이 여기에 반비례한다. */
  zoom?: number
  /**
   * 반경 직접 지정(px). 지정하면 `zoom`보다 우선한다. 제품 경로에서는 바꾸기 중 물건 고르기가 `EXACT_TAP_RADIUS_PX`(0)로
   * 링을 끄는 데 쓴다(머리말 "바꾸기의 물건 고르기") - 그 밖에는 튜닝·테스트용이다.
   */
  radiusPx?: number
}>

/**
 * 탭 판정 결과.
 *
 * `picker.ts`에도 같은 이름의 타입이 있지만 **다른 것**이다.
 * 그쪽은 "포인터가 가리키는 셀"(배치용 한 가지)이고, 이쪽은 6.1절의 판정 경로를 구별하는 유니온이다.
 */
export type PickResult =
  | Readonly<{
      kind: 'object'
      instanceId: InstanceId
      catalogId: CatalogId
      point: WorldPoint
      distance: number
      /** 탭 지점에서 몇 px 떨어진 샘플이 잡혔는가. 0이면 정확히 누른 것이다(연출·튜닝 지표). */
      offsetPx: number
    }>
  | Readonly<{
      kind: 'removed'
      /**
       * **빈 자리**를 맞혔다. 이름이 `removed`인 것은 오래된 이름이고, 지금은 제거된 자리와 **옮기기 전
       * 자리** 둘 다 이 갈래로 온다(`render/hitProxy.ts` 머리말). 어느 쪽인지는 이 층이 판단하지 않는다 -
       * 그 물건에 어떤 변화가 걸려 있는가는 판정기(`game/hunt.resolveShot`)만 아는 사실이다.
       */
      instanceId: InstanceId
      catalogId: CatalogId
      point: WorldPoint
      distance: number
      offsetPx: number
    }>
  | Readonly<{
      kind: 'empty'
      /** 눌린 표면 바로 앞의 셀. 6.2절에 따라 서버가 이 셀을 제거 정답 영역과 대조한다. */
      cell: Cell3
      point: WorldPoint
      distance: number
      offsetPx: number
    }>
  | Readonly<{
      kind: 'place'
      cell: Cell3
      /** 3.2절의 표면 스냅으로 계산됐는가. 탭 경로는 항상 true다(자유 높이는 수정키 전용). */
      isSurfaceSnap: boolean
      /** 표면을 제공한 오브젝트. 바닥이면 없다. */
      overInstanceId?: InstanceId
      point: WorldPoint
      distance: number
    }>

/* --- 내부 타입 ----------------------------------------------------------------- */

/** 광선 한 발이 만난 것. `surface`는 벽·바닥·천장처럼 광선을 멈추는 셸 표면이다. */
type SampleHit =
  | Readonly<{
      kind: 'proxy'
      instanceId: InstanceId
      catalogId: CatalogId
      point: THREE.Vector3
      distance: number
    }>
  | Readonly<{
      kind: 'instance'
      obj: ObjectInstance
      entry: CatalogEntry
      point: THREE.Vector3
      distance: number
    }>
  | Readonly<{
      kind: 'surface'
      point: THREE.Vector3
      distance: number
      /** 이 샘플을 만든 광선의 방향. 셀 계산에서 면 뒤로 떨어지지 않게 물러날 때 쓴다. */
      direction: THREE.Vector3
    }>

/** 링 탐색에서 고른 후보와 그 샘플이 있던 화면상 거리. */
type RingPick = Readonly<{ hit: SampleHit; offsetPx: number }>

/* --- 순수 계산 ----------------------------------------------------------------- */

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * 줌 레벨에 반비례하는 관대한 히트 반경(CSS px).
 * 줌 0(전체 조망)에서 가장 크고 줌 1(최대 확대)에서 가장 작다.
 */
export function tapRadiusPx(zoom: number): number {
  const normalized = Number.isFinite(zoom) ? clamp(zoom, 0, 1) : 0

  return (
    TAP_RADIUS_AT_MIN_ZOOM_PX +
    (TAP_RADIUS_AT_MAX_ZOOM_PX - TAP_RADIUS_AT_MIN_ZOOM_PX) * normalized
  )
}

function toWorldPoint(point: THREE.Vector3): WorldPoint {
  return { x: point.x, y: point.y, z: point.z }
}

/** 셀의 x·z가 방 안에 있는가. 높이는 보지 않는다 - 배치 가능 여부는 `canPlace`의 몫이다. */
function isColumnInsideRoom(cell: Cell3, bounds: RoomBounds): boolean {
  return cell.x >= 0 && cell.x < bounds.sizeX && cell.z >= 0 && cell.z < bounds.sizeZ
}

/** 셀 전체가 방 안에 있는가. 빈 셀 히트는 방 밖 좌표를 서버로 보내면 안 되므로 y까지 본다. */
function isCellInsideRoom(cell: Cell3, bounds: RoomBounds): boolean {
  return isColumnInsideRoom(cell, bounds) && cell.y >= 0 && cell.y < bounds.sizeY
}

/**
 * 조상까지 거슬러 올라가며 실제로 화면에 그려지는지 확인한다.
 *
 * 아이소메트릭에서 이 검사가 특히 중요하다 -
 * 컷어웨이가 앞쪽 벽·천장을 숨기는데, 그것들이 광선을 막으면
 * **화면에 보이는 물건을 누를 수 없게 된다.** 원칙 2의 정면 위반이다.
 *
 */
function isEffectivelyVisible(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object

  while (current !== null) {
    if (!current.visible) return false
    current = current.parent
  }

  return true
}

/**
 * **광선이 맞은 그 면이 화면에 그려지는가.**
 *
 * 노드 단위로는 답할 수 없다. 컷어웨이는 벽을 `mesh.visible`이 아니라 **머티리얼 그룹 단위**로 끄고
 * (`render/cutaway.ts` - 셸의 벽 하나가 다중 머티리얼 메시다. 실측으로 `shell-wall-living`이
 * `[보임, 숨김, 보임, 숨김, 숨김]`이었다), Three의 레이캐스터는 머티리얼을 보지 않는다. 그래서 조상
 * `visible`만 검사하면 **화면에 없는 벽면이 광선을 막는다** - 그 벽 뒤 소품은 눈에 훤히 보이는데 눌리지
 * 않고, 수색에서는 그것이 곧 못 쏘는 정답이다. 메시 단위로 "하나라도 보이면 통과"라고 물어도 같은 결과다 -
 * 벽 하나에 보이는 면과 숨은 면이 늘 섞여 있기 때문이다. 물어야 하는 것은 **맞은 면**이다.
 *
 * 페이드 중(0 < 알파 < 1)은 막는 것이 맞다 - 그때는 벽이 실제로 화면에 있고, 그 구간은 전환 한 번의
 * 짧은 순간이다. 여기서 보는 것은 "그려지는가"이지 "얼마나 진한가"가 아니다.
 *
 * ## 판정 상자만은 예외다 - 그것이 그 상자의 존재 이유다
 *
 * 같은 `material.visible = false`가 **정반대 뜻**으로도 쓰인다. `render/meshFactory.createHitCollider`는
 * 작은 소품을 누를 수 있게 보이지 않는 상자를 씌우면서, 노드가 아니라 머티리얼을 끈다고 머리말에 적어 두었다 -
 * "렌더 목록에서만 빠지고 판정은 살아 있다"가 그 상자의 계약이다. 그러므로 가르는 것은 "머티리얼이 꺼졌는가"가
 * 아니라 **"안 보이는 채로 눌리기로 한 물건인가"**다. 실측으로 방 하나에 그런 상자가 113개였고, 이 예외가
 * 없으면 소품 113개가 통째로 안 눌린다.
 */
function isFaceDrawn(intersection: THREE.Intersection): boolean {
  const object = intersection.object
  if (!(object instanceof THREE.Mesh) || isHitCollider(object)) return true

  const material: unknown = object.material
  if (!Array.isArray(material)) return material instanceof THREE.Material ? material.visible : true

  // 다중 머티리얼은 맞은 면의 그룹만 본다. 그룹을 모르면(면 정보가 없는 지오메트리) 막는 쪽으로 둔다.
  const index = intersection.face?.materialIndex
  if (index === undefined) return true
  const hit: THREE.Material | undefined = material[index]
  return hit === undefined ? true : hit.visible
}

/* --- 광선 한 발 ---------------------------------------------------------------- */

/**
 * NDC 한 점으로 광선을 쏘고 **처음 의미를 갖는 교차**를 돌려준다.
 *
 * 판정 순서: 히트 프록시 → 보이는 Mesh → 그 외 통과.
 * 다른 점은 셸 표면을 '빗맞음'이 아니라 `surface` 히트로 살려 둔다는 것 하나다 -
 * 아이소메트릭에서는 바닥을 누르는 것이 곧 6.1절의 **빈 셀 히트**이기 때문이다.
 */
function castSample(
  ctx: PickerCtx,
  ndc: THREE.Vector2,
  state: RoomState,
  catalog: Catalog,
  includeProxies: boolean,
): SampleHit | null {
  ctx.raycaster.setFromCamera(ndc, ctx.camera)
  const direction = ctx.raycaster.ray.direction.clone()

  for (const intersection of ctx.raycaster.intersectObject(ctx.scene, true)) {
    const target = intersection.object

    // 프록시는 보이지 않는데도 맞아야 하는 유일한 대상이라 가시성 검사보다 먼저 본다(6.2절).
    const proxy = readHitProxyInfo(target)
    if (proxy !== undefined) {
      if (!includeProxies) continue
      return {
        kind: 'proxy',
        instanceId: proxy.originInstanceId,
        catalogId: proxy.catalogId,
        point: intersection.point,
        distance: intersection.distance,
      }
    }

    // 화면에 없는 것은 탭과 상호작용하지 않는다(컷어웨이로 숨긴 벽·천장, 배치 고스트).
    // 벽은 **면 단위**로 숨으므로 노드와 면을 둘 다 본다(`isFaceDrawn` 머리말).
    if (!isEffectivelyVisible(target) || !isFaceDrawn(intersection)) continue

    // 선·점은 물체가 아니다. Three의 기본 임계값이 1m라 스치기만 해도 잡히므로 반드시 거른다.
    if (!(target instanceof THREE.Mesh)) continue

    const surface: SampleHit = {
      kind: 'surface',
      point: intersection.point,
      distance: intersection.distance,
      direction,
    }

    const instanceId = getInstanceIdFromObject3D(target)
    if (instanceId === undefined) return surface

    // 씬이 상태보다 한 프레임 뒤처져 이미 제거된 오브젝트를 들고 있을 수 있다.
    // 진실은 상태 쪽이지만 화면에는 아직 보이므로 광선은 여기서 멈춘 것으로 친다.
    const obj = state.objects.get(instanceId)
    if (obj === undefined) return surface

    const entry = catalog.get(obj.catalogId)
    if (entry === undefined) {
      // 조용히 넘기면 "보이는데 안 눌리는 소품"이 되어 원인 추적이 불가능해진다.
      // 탭당 한 번이라 로그 비용도 문제되지 않는다.
      console.warn(
        `[tapPicker] 카탈로그에 없는 catalogId '${obj.catalogId}' (인스턴스 '${instanceId}'). 히트로 인정하지 않는다.`,
      )
      return surface
    }

    return { kind: 'instance', obj, entry, point: intersection.point, distance: intersection.distance }
  }

  return null
}

/** 클라이언트 좌표 → NDC. 캔버스가 아직 배치되지 않았으면 `null`(가리키는 것 없음). */
function toNdc(rect: ViewportRect, clientX: number, clientY: number): THREE.Vector2 | null {
  if (rect.width <= 0 || rect.height <= 0) return null

  return new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * NDC_RANGE_SCALE - 1,
    -(((clientY - rect.top) / rect.height) * NDC_RANGE_SCALE) + 1,
  )
}

/* --- 수색 모드 ----------------------------------------------------------------- */

/**
 * 주변 8방향을 훑어 가장 앞쪽(카메라에 가까운) 후보를 고른다.
 * 프록시와 오브젝트를 따로 모으는 이유는 프록시가 항상 이겨야 하기 때문이다(파일 상단 우선순위 표).
 */
function probeRing(
  ctx: PickerCtx,
  rect: ViewportRect,
  x: number,
  y: number,
  radiusPx: number,
  state: RoomState,
  catalog: Catalog,
): { proxy: RingPick | null; instance: RingPick | null } {
  let proxy: RingPick | null = null
  let instance: RingPick | null = null

  for (const direction of RING_DIRECTIONS) {
    const [dx, dy] = direction
    const ndc = toNdc(rect, x + dx * radiusPx, y + dy * radiusPx)
    if (ndc === null) continue

    const hit = castSample(ctx, ndc, state, catalog, true)
    if (hit === null || hit.kind === 'surface') continue

    const candidate: RingPick = { hit, offsetPx: radiusPx }
    if (hit.kind === 'proxy') {
      if (proxy === null || hit.distance < proxy.hit.distance) proxy = candidate
      continue
    }
    if (instance === null || hit.distance < instance.hit.distance) instance = candidate
  }

  return { proxy, instance }
}

function toHitResult(hit: SampleHit, offsetPx: number): PickResult | null {
  if (hit.kind === 'proxy') {
    return {
      kind: 'removed',
      instanceId: hit.instanceId,
      catalogId: hit.catalogId,
      point: toWorldPoint(hit.point),
      distance: hit.distance,
      offsetPx,
    }
  }

  if (hit.kind === 'instance') {
    return {
      kind: 'object',
      instanceId: hit.obj.id,
      catalogId: hit.obj.catalogId,
      point: toWorldPoint(hit.point),
      distance: hit.distance,
      offsetPx,
    }
  }

  return null
}

/** 셸 표면 히트 → 그 면 **바로 앞**의 셀. 방을 벗어나면 `null`. */
function emptyCellResult(
  hit: Extract<SampleHit, { kind: 'surface' }>,
  bounds: RoomBounds,
): PickResult | null {
  const backed = hit.point.clone().addScaledVector(hit.direction, -SURFACE_BACKOFF_METERS)
  const cell = worldToCell(backed.x, backed.y, backed.z)
  if (!isCellInsideRoom(cell, bounds)) return null

  return {
    kind: 'empty',
    cell,
    point: toWorldPoint(hit.point),
    distance: hit.distance,
    offsetPx: 0,
  }
}

function resolveSearch(
  ctx: PickerCtx,
  rect: ViewportRect,
  x: number,
  y: number,
  state: RoomState,
  catalog: Catalog,
  bounds: RoomBounds,
  radiusPx: number,
): PickResult | null {
  const centerNdc = toNdc(rect, x, y)
  if (centerNdc === null) return null

  const center = castSample(ctx, centerNdc, state, catalog, true)

  // 중심이 확신을 준 경우. 주변을 볼수록 정확히 겨눈 탭을 망칠 뿐이다.
  const centerResult = center === null ? null : toHitResult(center, 0)
  if (centerResult !== null) return centerResult

  // 반경 0은 "구제하지 않는다"다. 같은 점을 다시 쏘면 결과도 같으므로 링 자체를 건너뛴다.
  if (radiusPx > 0) {
    const ring = probeRing(ctx, rect, x, y, radiusPx, state, catalog)
    if (ring.proxy !== null) return toHitResult(ring.proxy.hit, ring.proxy.offsetPx)
    if (ring.instance !== null) return toHitResult(ring.instance.hit, ring.instance.offsetPx)
  }

  if (center !== null && center.kind === 'surface') return emptyCellResult(center, bounds)

  return null
}

/* --- 배치 모드 ----------------------------------------------------------------- */

/** 광선과 바닥 평면(y = 0)의 교차. 카메라 뒤이거나 평행하면 `null`. */
function intersectFloorPlane(
  ctx: PickerCtx,
  bounds: RoomBounds,
): { cell: Cell3; point: THREE.Vector3; distance: number } | null {
  const plane = new THREE.Plane(
    new THREE.Vector3(UP_AXIS.x, UP_AXIS.y, UP_AXIS.z),
    -FLOOR_PLANE_Y,
  )
  const point = new THREE.Vector3()
  if (ctx.raycaster.ray.intersectPlane(plane, point) === null) return null

  const column = worldToCell(point.x, point.y, point.z)
  const cell: Cell3 = { x: column.x, y: 0, z: column.z }
  if (!isColumnInsideRoom(cell, bounds)) return null

  return { cell, point, distance: ctx.raycaster.ray.origin.distanceTo(point) }
}

/**
 * 오브젝트 윗면에 얹히는 셀(3.2절 표면 스냅).
 * 높이는 맞은 지점이 아니라 **오브젝트의 꼭대기**로 잡는다 - 옆면을 눌러도 위에 올라가야
 * "책상을 가리키면 그 위에 붙는다"가 성립한다. x·z는 footprint 안으로 가둔다
 * (모서리를 정확히 맞히면 내림 연산이 한 칸 바깥을 내놓는다).
 */
function surfaceCellOnObject(hit: Extract<SampleHit, { kind: 'instance' }>): Cell3 {
  const size = rotatedFootprint(hit.entry.footprint, hit.obj.rot)
  const pointed = worldToCell(hit.point.x, hit.point.y, hit.point.z)

  return {
    x: clamp(pointed.x, hit.obj.cell.x, hit.obj.cell.x + size.x - 1),
    y: hit.obj.cell.y + size.y,
    z: clamp(pointed.z, hit.obj.cell.z, hit.obj.cell.z + size.z - 1),
  }
}

function resolvePlace(
  ctx: PickerCtx,
  rect: ViewportRect,
  x: number,
  y: number,
  state: RoomState,
  catalog: Catalog,
  bounds: RoomBounds,
): PickResult | null {
  const ndc = toNdc(rect, x, y)
  if (ndc === null) return null

  // 프록시는 제외한다. 사라진 물건의 판정용 상자가 배치를 막으면 그 자리에 아무것도 못 놓는다.
  const hit = castSample(ctx, ndc, state, catalog, false)
  // `castSample`이 방금 맞춰 둔 광선을 그대로 쓴다.
  const floor = intersectFloorPlane(ctx, bounds)

  const instanceHit = hit !== null && hit.kind === 'instance' ? hit : null

  // 바닥이 오브젝트보다 앞에 있으면 그 오브젝트는 가려져 보이지 않는다. 안 보이는 표면에 올릴 수는 없다.
  const isObjectVisible =
    instanceHit !== null && (floor === null || instanceHit.distance <= floor.distance)

  if (isObjectVisible) {
    return {
      kind: 'place',
      cell: surfaceCellOnObject(instanceHit),
      isSurfaceSnap: true,
      overInstanceId: instanceHit.obj.id,
      point: toWorldPoint(instanceHit.point),
      distance: instanceHit.distance,
    }
  }

  if (floor === null) return null

  return {
    kind: 'place',
    cell: floor.cell,
    isSurfaceSnap: true,
    point: toWorldPoint(floor.point),
    distance: floor.distance,
  }
}

/* --- 공개 진입점 --------------------------------------------------------------- */

/**
 * 탭 좌표(클라이언트 좌표계, `pointerInput`의 `onTap`이 넘기는 값)를 게임 대상으로 해석한다.
 *
 * @returns 겨눈 것이 없으면 `null` - 방 바깥 허공을 누른 경우다. 호출부는 탄환을 소모하지 않는다.
 */
export function pickAt(
  ctx: PickerCtx,
  x: number,
  y: number,
  state: RoomState,
  catalog: Catalog,
  bounds: RoomBounds,
  options: PickOptions = {},
): PickResult | null {
  const rect = ctx.renderer.domElement.getBoundingClientRect()

  // Raycaster는 월드 행렬을 갱신해 주지 않는다. 탭은 프레임 사이에 도착하므로 방금 옮겨진 메시가
  // 낡은 행렬을 들고 있을 수 있고, 그러면 원점에 있는 것으로 잡혀 엉뚱한 판정이 난다.
  // 샘플마다가 아니라 여기서 한 번만 부른다(9발을 쏘므로 반복은 순수한 낭비다).
  ctx.scene.updateMatrixWorld()

  if ((options.mode ?? 'search') === 'place') {
    return resolvePlace(ctx, rect, x, y, state, catalog, bounds)
  }

  const radiusPx = options.radiusPx ?? tapRadiusPx(options.zoom ?? 0)
  return resolveSearch(ctx, rect, x, y, state, catalog, bounds, radiusPx)
}
